// Documento tributario que elige el repartidor (campo top-level `user.billingDocType`).
// Valores en DB: factura | boleta | null.
//   - Cambia el PAGO del repartidor: el backend aplica la retención/monto según el
//     documento y la liquidación ya llega ajustada; la web solo lo muestra y lo edita.
//   - `null` = el repartidor todavía no eligió documento; queda FUERA de la planilla
//     banco hasta que elija factura o boleta.
// Las etiquetas se traducen con t(); el fallback de i18n es la propia clave (español).
// Punto único de verdad reutilizado en la liquidación, el detalle del día y la edición.

// Opciones seleccionables (2): factura, boleta. `value` = valor de DB,
// `label` = clave i18n en español (natural key).
export const BILLING_DOC_OPTIONS = [
  { value: "factura", label: "Factura" },
  { value: "boleta", label: "Boleta" },
];

// Traduce un billingDocType a su etiqueta. `null`/ausente → "Sin documento".
// Un valor no reconocido se devuelve tal cual para no ocultar datos.
export function billingDocLabel(value, t) {
  const tr = typeof t === "function" ? t : (s) => s;
  if (!value) return tr("Sin documento");
  const opt = BILLING_DOC_OPTIONS.find((o) => o.value === value);
  return opt ? tr(opt.label) : value;
}

const isDocType = (value) => BILLING_DOC_OPTIONS.some((o) => o.value === value);

/**
 * Aviso de la liquidación cuando el tipo con que se paga NO es el del perfil actual.
 * Desde que billing paga cada semana con el tipo del documento de ESA semana (o el
 * vigente en ella), la fila trae `billingDocType` = tipo efectivo, `profileDocType` = el
 * del perfil hoy y `mixedDocTypes` = el rango mezcla semanas de tipos distintos. Sin esos
 * campos (backend anterior) no hay aviso.
 *
 * @param {object} d fila de drivers[] del summary
 * @param {(s: string, o?: object) => string} [t]
 * @returns {{ label: string, title: string } | null}
 */
export function docTypeMismatchLabel(d, t) {
  const tr = typeof t === "function" ? t : (s) => s;
  if (!d || d.isFreelancer) return null;
  const title = tr(
    "El pago de cada semana usa el tipo del documento de esa semana; un cambio en el perfil rige desde el domingo siguiente.",
  );
  if (d.mixedDocTypes === true) {
    return { label: tr("Semanas con tipos distintos"), title };
  }
  if (isDocType(d.profileDocType) && isDocType(d.billingDocType) && d.profileDocType !== d.billingDocType) {
    return {
      label: tr("Perfil: {{perfil}} · se paga como {{tipo}}", {
        perfil: billingDocLabel(d.profileDocType, tr),
        tipo: billingDocLabel(d.billingDocType, tr),
      }),
      title,
    };
  }
  return null;
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Hoy como 'YYYY-MM-DD' en una zona horaria (billing calcula el "próximo domingo" en Chile). */
export function todayYMDInTz(timeZone = "America/Santiago", now = new Date()) {
  try {
    // en-CA formatea como YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }
}

/** Domingo (inicio de la semana dom–sáb) de un 'YYYY-MM-DD', aritmética en UTC puro. */
export function sundayOf(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return date.toISOString().slice(0, 10);
}

/**
 * Cambio factura↔boleta del perfil que todavía no rige: el ÚLTIMO registro de
 * `billingDocTypeChanges` (por `changedAt`) cuyo `effectiveWeekStart` es posterior al
 * domingo de la semana en curso. Es la misma regla con que users arma `pendingChange`.
 *
 * @param {Array<{from?: string|null, to?: string, effectiveWeekStart?: string|null, changedAt?: string}>} changes
 * @param {string} [today] 'YYYY-MM-DD' (por defecto, hoy en America/Santiago)
 * @returns {{ from: string|null, to: string, effectiveWeekStart: string } | null}
 */
export function pendingBillingDocTypeChange(changes, today = todayYMDInTz()) {
  if (!Array.isArray(changes) || changes.length === 0 || !YMD_RE.test(String(today))) return null;
  const time = (c) => {
    const ms = Date.parse(c?.changedAt ?? "");
    return Number.isFinite(ms) ? ms : -Infinity;
  };
  // Orden estable por changedAt: ante empate (o sin fecha) manda el orden del arreglo.
  const last = changes
    .map((c, i) => ({ c, i }))
    .sort((a, b) => time(a.c) - time(b.c) || a.i - b.i)
    .pop()?.c;
  if (!last || !isDocType(last.to) || !YMD_RE.test(String(last.effectiveWeekStart ?? ""))) return null;
  if (last.effectiveWeekStart <= sundayOf(today)) return null;
  return {
    from: isDocType(last.from) ? last.from : null,
    to: last.to,
    effectiveWeekStart: last.effectiveWeekStart,
  };
}
